import { useFormik } from 'formik';
import { MimaToastUtil } from '../../../../components';
import {
	handleImageUpload,
	refreshQuery,
	removeEmptyKeys,
} from '../../../../utils/utils';
import useProductFormValidationSchema from './useProductFormValidationSchema';
import { AdminController } from '../../../../controllers';
import constant from '../../../../utils/constant';
import { adminStore } from '../../../../stores';
import { useNavigate } from 'react-router-dom';

export const useAdminAddProductsLogic = () => {
	const navigate = useNavigate();
	const { businessId } = adminStore;
	const { validationSchema } = useProductFormValidationSchema();
	const onSubmit = async values => {
		if (values.imageUrls?.length < 1) {
			return MimaToastUtil.error({
				message: 'Kindly Add atleast 1 image ',
			});
		}

		let payload;

		let urls = [];
		if (values.imageUrls.length > 0) {
			urls = await handleImageUpload(values.imageUrls);
		}

		const unifiedCollections = [
			...values.styleCollections,
			...values.newCollections,
		];

		const updatedVariations = await Promise.all(
			values.variations.map(
				async ({
					customColors,
					colorsAvailable,
					international,
					local,
					addCustomColor,
					isDigital,
					documentUrls,
					...rest
				}) => {
					const customColorsObjects = customColors?.map(color => ({
						color,
						type: 'CUSTOM',
					}));

					let urls = [];

					if (documentUrls?.length > 0) {
						urls = await handleImageUpload(documentUrls);
					}

					return {
						...rest,
						...(values.sellAbroad && values.pricingModel !== 'BESPOKE'
							? { international }
							: {}),
						...(values.pricingModel !== 'BESPOKE' ? { local } : {}),
						...(colorsAvailable?.length > 0 || customColorsObjects?.length > 0
							? {
									colorsAvailable: [
										...(colorsAvailable || []),
										...(customColorsObjects || []),
									].filter(item => item && Object.keys(item).length > 0),
								}
							: {}),
						isDigital,
						documentUrls: urls,
					};
				}
			)
		);

		const prePayload = {
			...values,
			variations: updatedVariations,
		};

		let typeUpdatedVariations;

		if (values.type === 'PRODUCT') {
			delete prePayload.availableDays;
			delete prePayload.availableWindow;

			typeUpdatedVariations = prePayload.variations.map(
				({ durationInMinutes, ...rest }) => rest
			);
		}

		if (values.type === 'SERVICE') {
			typeUpdatedVariations = [...prePayload.variations];
		}

		payload = {
			...prePayload,
			variations: typeUpdatedVariations,
			imageUrls: urls,
			styleCollections: unifiedCollections,
		};

		delete payload.createCollections;
		delete payload.hasVariations;
		delete payload.newCollections;
		delete payload.sellAbroad;

		if (!values.description) {
			delete payload.description;
		}

		removeEmptyKeys(payload);

		const { status, errorMessage } =
			await AdminController.createProduct(payload);

		if (status === constant.Success) {
			const id = businessId;
			refreshQuery({ queryKey: [id, constant.apiQueryKey.getProducts] });
			MimaToastUtil.success({
				message: constant.Success,
			});
			return navigate(constant.Routes.Admin.Products);
		}

		return MimaToastUtil.error({
			message: errorMessage,
		});
	};

	const initialValues = {
		//Step 1
		name: '',
		type: 'PRODUCT',
		bestSelling: false,
		topView: false,
		createCollections: false,
		styleCollections: [],
		newCollections: [],
		description: '',
		gender: '',

		//Step 2
		pricingModel: 'FIXED_PRICE',
		hasVariations: false,
		variations: [
			{
				sizes: [],
				name: '',
				isSoldOut: false,
				accessories: [],
				addCustomColor: false,
				customColors: [],
				colorsAvailable: [
					{
						color: '',
						type: '',
						code: '',
					},
				],
				location: '',
				local: {
					withoutMaterial: 0,
					currencyCode: 'NGN', //Temporary
					onSaleSlashPrice: 0,
					withMaterial: 0,
				},
				durationInMinutes: 0,
				international: {
					currencyCode: '',
					withMaterial: 0,
					onSaleSlashPrice: 0,
				},
				isDigital: false,
				documentUrls: [],
			},
		],
		sellAbroad: false,

		//Step 3
		imageUrls: [],
		availableWindow: {
			startHour: 0,
			endHour: 0,
		},
		availableDays: [],
		daysRequiredForDelivery: {
			early: 1,
			late: 1,
		},
	};

	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit,
	});

	return {
		formik,
	};
};

export default useAdminAddProductsLogic;
